// Editable working copy over the extraction result. Field edits mutate the
// drafts in place (an input keeps its own DOM state — re-rendering the list on
// every keystroke would steal the focus); only STRUCTURAL changes (add/remove/
// reorder) go through the signals. Same model as the source UI's working copy.
import { Adjustment, Bbox, ItemValidation } from './extraction-result';
import { Box } from '../utils/geometry';

export const POSITION_FIELDS = [
  'item_number',
  'product_number',
  'ean',
  'description',
  'quantity',
  'unit',
  'unit_price',
  'net',
  'vat_rate',
  'discount',
] as const;
export type PositionField = (typeof POSITION_FIELDS)[number];

export type PageBox = Box & { page: number };

/**
 * One position row. The list is a FLAT array in PRE-ORDER and `depth` is the
 * only structural truth — there are no child arrays. Invariants every
 * structural operation must restore (see utils/position-tree.ts):
 *
 * - `depth[0] === 0` and `depth[i] <= depth[i - 1] + 1` (no level is skipped);
 * - the subtree of `i` is the half-open range `[i, subtreeEnd(i))`, so a
 *   parent can never be moved or removed without its descendants;
 * - the row selection is remapped by draft IDENTITY after every structural
 *   change — flat indexes shift, the drafts themselves do not.
 *
 * `id` is that identity in the template (`track`) and for the collapse set;
 * ProductMatchService keys its verdicts by the draft object itself.
 */
export interface PositionDraft {
  /** Stable within one working copy ("pos-3"); never sent to a backend. */
  id: string;
  /** Editable values; `discount` is the projected column of `discounts`. */
  fields: Record<PositionField, string>;
  /** Raw document text per field (tooltip), keyed like `fields`. */
  raws: Partial<Record<PositionField, string>>;
  /** Field boxes assigned via marking or delivered by the backend. */
  boxes: Partial<Record<PositionField, PageBox>>;
  /** Pristine adjustment list of the row (kept for the plausibility input). */
  discounts: Adjustment[];
  validation: ItemValidation | null;
  page: number;
  rowbbox: Bbox | null;
  depth: number;
}

export interface MetadataDraft {
  /** Result path ("metadata.supplier.name", "items[0].net", …) or `new-<n>`. */
  key: string;
  value: string;
  box: PageBox | null;
  raw?: string;
  /** Derived root-adjustment entries are visible but not editable. */
  readonly?: boolean;
  /** Newly added field — the category is chosen via `label`. */
  isNew?: boolean;
  /** Category of a new field (e.g. "cash_discount1"). */
  label?: string;
  /** Metadata group a new field was added to. */
  group?: MetadataGroup;
  /** Present on root-adjustment entries: which entry + which half. */
  adjustment?: Adjustment;
  adjustmentIndex?: number;
  adjustmentHalf?: 'value' | 'days';
}

export const METADATA_GROUPS = [
  'metadata.supplier',
  'metadata.recipient',
  'metadata.receipt',
  'items[0]',
] as const;
export type MetadataGroup = (typeof METADATA_GROUPS)[number];

/** Root-item fields that are document values (the rest is tree/diagnostics). */
export const ROOT_METADATA_FIELDS = [
  'net',
  'vat_rate',
  'vat',
  'gross',
  'currency',
] as const;

/** Skonto-stage categories a user can add (feed the plausibility check). */
export const CASH_DISCOUNT_CATEGORIES = [
  'cash_discount1',
  'days1',
  'cash_discount2',
  'days2',
] as const;

export type RowSelection = { kind: 'rows'; indexes: ReadonlySet<number> };
export type FieldSelection =
  | { kind: 'position-field'; index: number; field: PositionField }
  | { kind: 'metadata-field'; key: string };
export type Selection = RowSelection | FieldSelection | null;

/** Target of an armed ⌖ box marking. */
export type MarkTarget =
  | { kind: 'position'; index: number; field: PositionField; label?: string }
  | { kind: 'metadata'; key: string; label?: string };
